import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Skeleton,
  Alert,
  Chip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Photo as PhotoIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import { DashboardService } from '../../services/dashboardService';
import { Person, Photo } from '../../types';
import { useNavigate } from 'react-router-dom';

/**
 * FamilyHighlightsCard Component
 *
 * Displays important family information in tabs
 *
 * Tabs:
 * 1. Incomplete Profiles - Profiles needing attention
 * 2. Recent Photos - Latest uploaded photos
 * 3. Upcoming Birthdays - Birthdays in next 30 days
 *
 * @component
 * @returns {JSX.Element} Rendered highlights card
 */
export const FamilyHighlightsCard: React.FC = () => {
  // ===== State =====
  const [activeTab, setActiveTab] = useState(0);
  const [incompleteProfiles, setIncompleteProfiles] = useState<Person[]>([]);
  const [recentPhotos, setRecentPhotos] = useState<Photo[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // ===== Effects =====
  useEffect(() => {
    fetchHighlights();
  }, []);

  // ===== Helper Functions =====
  const fetchHighlights = async () => {
    try {
      setLoading(true);
      setError(null);

      const [incomplete, photos, birthdays] = await Promise.all([
        DashboardService.getIncompleteProfiles(5),
        DashboardService.getRecentPhotos(6),
        DashboardService.getUpcomingBirthdays(30),
      ]);

      setIncompleteProfiles(incomplete);
      setRecentPhotos(photos);
      setUpcomingBirthdays(birthdays);
    } catch (err: any) {
      console.error('Failed to load highlights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPersonName = (person: Person): string => {
    return `${person.firstName} ${person.lastName}`.trim();
  };

  const getBirthdayText = (person: any): string => {
    const days = person.daysUntil;
    if (days === 0) return 'Today!';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  // ===== Event Handlers =====
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePersonClick = (personId: string) => {
    navigate(`/people/${personId}`);
  };

  // ===== Render Loading =====
  if (loading) {
    return (
      <Card>
        <CardHeader title="Family Highlights" />
        <CardContent>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  // ===== Render Error =====
  if (error) {
    return (
      <Card>
        <CardHeader title="Family Highlights" />
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  // ===== Render =====
  return (
    <Card>
      <CardHeader title="Family Highlights" />
      <CardContent>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon fontSize="small" />
                Incomplete
                <Chip label={incompleteProfiles.length} size="small" color="warning" />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PhotoIcon fontSize="small" />
                Recent Photos
                <Chip label={recentPhotos.length} size="small" color="info" />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CakeIcon fontSize="small" />
                Birthdays
                <Chip label={upcomingBirthdays.length} size="small" color="success" />
              </Box>
            }
          />
        </Tabs>

        {/* Tab 0: Incomplete Profiles */}
        {activeTab === 0 && (
          <Box>
            {incompleteProfiles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  All profiles are complete!
                </Typography>
              </Box>
            ) : (
              <List>
                {incompleteProfiles.map(person => (
                  <ListItem
                    key={person.id}
                    button
                    onClick={() => handlePersonClick(person.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ backgroundColor: 'warning.light', color: 'warning.main' }}>
                        {person.firstName?.[0]}{person.lastName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getPersonName(person)}
                      secondary={
                        <Box component="span" display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                          {!person.biography && (
                            <Chip label="No biography" size="small" variant="outlined" />
                          )}
                          {!person.birthDate && (
                            <Chip label="No birth date" size="small" variant="outlined" />
                          )}
                          {!person.birthPlace && (
                            <Chip label="No birth place" size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Tab 1: Recent Photos */}
        {activeTab === 1 && (
          <Box>
            {recentPhotos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No photos uploaded yet
                </Typography>
              </Box>
            ) : (
              <ImageList cols={3} gap={8} sx={{ mt: 0 }}>
                {recentPhotos.map(photo => (
                  <ImageListItem
                    key={photo.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => {
                      // Navigate to first tagged person's profile, or just show photo
                      if (photo.taggedPersons && photo.taggedPersons.length > 0) {
                        navigate(`/people/${photo.taggedPersons[0].personId}/photos`);
                      }
                    }}
                  >
                    <img
                      src={photo.thumbnailURL || photo.storageURL}
                      alt={photo.caption || 'Family photo'}
                      loading="lazy"
                      style={{
                        height: 150,
                        objectFit: 'cover',
                      }}
                    />
                    <ImageListItemBar
                      title={photo.caption || 'Photo'}
                      subtitle={photo.dateTaken ? new Date(photo.dateTaken).toLocaleDateString() : ''}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        )}

        {/* Tab 2: Upcoming Birthdays */}
        {activeTab === 2 && (
          <Box>
            {upcomingBirthdays.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No upcoming birthdays in the next 30 days
                </Typography>
              </Box>
            ) : (
              <List>
                {upcomingBirthdays.map(person => (
                  <ListItem
                    key={person.id}
                    button
                    onClick={() => handlePersonClick(person.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ backgroundColor: 'success.light', color: 'success.main' }}>
                        <CakeIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getPersonName(person)}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            {new Date(person.birthDate).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                            })}
                          </Typography>
                          <Chip
                            label={getBirthdayText(person)}
                            size="small"
                            color={person.daysUntil === 0 ? 'success' : 'default'}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
